import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '@/contexts';
import { getSessionId } from '@/utils/helpers';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ArrowLeft, Package, Eye, ShoppingCart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const { token } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const sessionId = getSessionId();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const safe = (p) => p.catch(() => ({ data: [] }));
        const [ordersRes, artworkRes] = await Promise.all([
          safe(axios.get(`${API}/orders?session_id=${sessionId}`, { headers })),
          token ? safe(axios.get(`${API}/artwork-purchase/my-orders`, { headers })) : { data: [] }
        ]);
        // Merge and sort by date
        const regularOrders = (ordersRes.data || []).map(o => ({ ...o, order_source: 'checkout' }));
        const artworkOrders = (artworkRes.data || []).map(o => ({
          ...o,
          order_source: 'artwork_purchase',
          order_number: o.order_number || o.id,
          total_amount: o.price || o.amount,
          items: [{ product_name: o.artwork_title, quantity: 1, price: o.price || o.amount }]
        }));
        const allOrders = [...regularOrders, ...artworkOrders].sort((a, b) => 
          new Date(b.created_at || 0) - new Date(a.created_at || 0)
        );
        setOrders(allOrders);
      } catch (err) {
        console.error('Failed to fetch orders', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [token]);

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: t('orders.status.pending'), color: 'bg-yellow-100 text-yellow-700' },
      paid: { label: t('orders.status.paid'), color: 'bg-blue-100 text-blue-700' },
      processing: { label: t('orders.status.processing'), color: 'bg-purple-100 text-amber-700' },
      shipped: { label: t('orders.status.shipped'), color: 'bg-indigo-100 text-indigo-700' },
      delivered: { label: t('orders.status.delivered'), color: 'bg-amber-100 text-amber-700' },
      cancelled: { label: t('orders.status.cancelled'), color: 'bg-red-100 text-red-700' },
    };
    const s = statusMap[status] || { label: status, color: 'bg-amber-100 text-amber-700' };
    return <Badge className={s.color}>{s.label}</Badge>;
  };

  const openOrderDetail = (order) => {
    setSelectedOrder(order);
    setShowDetail(true);
  };

  const getLocaleString = () => {
    return i18n.language === 'zh' ? 'zh-CN' : i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">{t('common.loading')}</div>;
  }

  return (
    <div className="min-h-screen bg-amber-50/50 py-8" data-testid="orders-page">
      <div className="container mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.backToHome')}
        </Button>
        <h1 className="text-2xl font-bold mb-8">{t('orders.title')}</h1>
        
        {orders.length === 0 ? (
          <Card className="text-center p-12">
            <Package className="w-16 h-16 mx-auto text-amber-500 mb-4" />
            <h3 className="text-lg font-medium mb-2">{t('orders.noOrders')}</h3>
            <p className="text-amber-600 mb-4">{t('orders.goShopping')}</p>
            <Button onClick={() => navigate('/')} className="bg-amber-600 hover:bg-amber-700">{t('cart.goShopping')}</Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Card key={order.id} data-testid={`order-card-${order.id}`}>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600">{t('orders.orderNumber')}: {order.order_number}</p>
                    <p className="text-sm text-amber-600">
                      {new Date(order.created_at).toLocaleString(getLocaleString())}
                    </p>
                  </div>
                  {getStatusBadge(order.status)}
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4 overflow-x-auto pb-2">
                    {order.items.slice(0, 4).map((item) => (
                      <div key={item.product_id} className="flex-shrink-0">
                        <img src={item.product_image} alt={item.product_name} className="w-20 h-20 object-cover rounded" />
                      </div>
                    ))}
                    {order.items.length > 4 && (
                      <div className="w-20 h-20 bg-amber-100 rounded flex items-center justify-center text-amber-600">
                        +{order.items.length - 4}
                      </div>
                    )}
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between items-center">
                  <div>
                    <span className="text-amber-600">{t('orders.totalItems', { count: order.items.reduce((sum, i) => sum + i.quantity, 0) })}</span>
                    <span className="mx-2">|</span>
                    <span className="font-bold text-amber-600">¥{(order.total_amount ?? 0).toFixed(2)}</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => openOrderDetail(order)} data-testid={`view-order-detail-${order.id}`}>
                    <Eye className="w-4 h-4 mr-1" /> {t('orders.viewDetail')}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Order Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('orders.orderDetail')}</DialogTitle>
            <DialogDescription>
              {t('orders.orderNumber')}: {selectedOrder?.order_number}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Order Status */}
              <div className="flex items-center justify-between p-4 bg-amber-50/50 rounded-lg">
                <div>
                  <p className="text-sm text-amber-600">{t('orders.orderStatus')}</p>
                  <div className="mt-1">{getStatusBadge(selectedOrder.status)}</div>
                </div>
                <div>
                  <p className="text-sm text-amber-600">{t('orders.paymentStatus')}</p>
                  <Badge className={selectedOrder.payment_status === 'paid' ? 'bg-amber-100 text-amber-700' : 'bg-yellow-100 text-yellow-700'}>
                    {selectedOrder.payment_status === 'paid' ? t('orders.paid') : t('orders.unpaid')}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-amber-600">{t('orders.orderTime')}</p>
                  <p className="font-medium">{new Date(selectedOrder.created_at).toLocaleString(getLocaleString())}</p>
                </div>
              </div>

              {/* Shipping Address */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Package className="w-4 h-4" /> {t('orders.shippingInfo')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-amber-600">{t('orders.recipient')}</p>
                      <p className="font-medium">{selectedOrder.shipping_address?.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-amber-600">{t('checkout.phone')}</p>
                      <p className="font-medium">{selectedOrder.shipping_address?.phone}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-amber-600">{t('orders.shippingAddress')}</p>
                    <p className="font-medium">
                      {selectedOrder.shipping_address?.province} {selectedOrder.shipping_address?.city} {selectedOrder.shipping_address?.district} {selectedOrder.shipping_address?.address}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Order Items */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4" /> {t('orders.productInfo')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-4 p-3 bg-amber-50/50 rounded-lg">
                        <img loading="lazy"
                          src={item.product_image} 
                          alt={item.product_name} 
                          className="w-16 h-16 object-cover rounded"
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/64?text=No+Image'; }}
                        />
                        <div className="flex-1">
                          <p className="font-medium">{item.product_name}</p>
                          <p className="text-sm text-amber-600">
                            {t('orders.unitPrice')}: ¥{item.price?.toFixed(2)} × {item.quantity}
                          </p>
                        </div>
                        <p className="font-bold text-amber-600">¥{item.subtotal?.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                  <Separator className="my-4" />
                  <div className="flex justify-between items-center">
                    <span className="text-amber-600">{t('checkout.subtotal')}</span>
                    <span className="text-xl font-bold text-amber-600">¥{selectedOrder.total_amount?.toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Order Note */}
              {selectedOrder.note && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{t('checkout.orderNote')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-amber-700/80">{selectedOrder.note}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersPage;
